import React, { useState, memo, useCallback } from 'react';
import Box from '@mui/material/Box';
import TableCell from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';
import { MenuVerticalIcon } from '~/assets/icons';
import styles from './FranchiseList.module.scss';
import classNames from 'classnames/bind';
import { Link } from '@mui/material';
import BaseTable from '~/components/common/base-table/BaseTable';
import { IFranchiseList } from '~/utils/interface/franchise';
import { HeadCelType, SortDirection } from '~/utils/types/common';
import MenuOption from '../menu-option/MenuOption';
import { useTranslation } from 'react-i18next';
import { franchiseActions } from '~/thunks/franchise/franchiseSlice';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import usePermission from '~/hooks/usePermission';
import { PERMISSIONS } from '~/utils/constants/permission';

const cx = classNames.bind(styles);

type FranchiseListProps = {
  onOpenEdit: (id: string) => void;
};

function FranchiseList(props: FranchiseListProps) {
  const permissions = usePermission();
  const { t } = useTranslation('superadmin/franchise');
  const headCells: Array<HeadCelType> = [
    {
      id: 'name',
      label: t(`name`),
      sortable: true,
      searchable: true,
    },
    {
      id: 'admin',
      label: t(`admin`),
      sortable: false,
      searchable: false,
    },
    {
      id: 'totalJob',
      label: t(`totalproject`),
      sortable: true,
      searchable: true,
    },
    {
      id: '',
      label: '',
      sortable: false,
      searchable: false,
    },
    {
      id: '1',
      label: '',
      sortable: false,
      searchable: false,
    },
  ];
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const dispatch = useAppDispatch();
  const { franchises, currentPage, totalPages, selectedIds, menuSelectedId } = useAppSelector(
    (state) => state.franchise
  );
  const openOptionMenu = (event: React.MouseEvent<HTMLButtonElement>, itemId: string) => {
    setAnchorEl(event.currentTarget);
    dispatch(franchiseActions.changeMenuSelectId(itemId));
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const setSelectedIds = useCallback(
    (selected: Array<string>) => {
      dispatch(franchiseActions.changeSelectedIds(selected));
    },
    [dispatch]
  );
  const handleEditItem = () => {
    handleClose();
    if (menuSelectedId !== undefined) {
      props.onOpenEdit(menuSelectedId);
    }
  };

  const handleDeleteItem = () => {
    handleClose();
    if (selectedIds !== undefined) {
      dispatch(franchiseActions.changeOpenAlertDelete(true));
    }
  };

  const handleRequestSort = (sortBy: Array<{ orderBy: string; order: SortDirection }>) => {
    dispatch(
      franchiseActions.setFilter({
        sort: sortBy.map((item) => ({
          field: item.orderBy,
          type: item.order,
        })),
      })
    );
  };

  const handleChangePage = (newPage: number) => {
    dispatch(
      franchiseActions.setFilter({
        page: newPage,
      })
    );
  };

  return (
    <Box className={cx('franchise-list-container')}>
      <BaseTable<IFranchiseList>
        data={franchises}
        setSelectedIds={setSelectedIds}
        headCells={headCells}
        onChangePage={handleChangePage}
        page={currentPage}
        onRequestSort={handleRequestSort}
        totalPages={totalPages}
        renderCell={(franchises: IFranchiseList) => {
          return (
            <>
              <TableCell component='th' scope='row' padding='none'>
                <Box className={cx('name-container')}>
                  <Box className={cx('name')}>
                    <Link className='text-[#3A3F51] hover:text-[#007AFC]'>{franchises.name}</Link>
                  </Box>
                </Box>
              </TableCell>

              <TableCell>
                <Box className={cx('assigness-container')}>
                  <Box className={cx('name')}>{franchises.admin}</Box>
                </Box>
              </TableCell>

              <TableCell>
                <Box className={cx('total-project')}>
                  <Box>{franchises.totalJob}</Box>
                </Box>
              </TableCell>

              {(permissions[PERMISSIONS.company_edit] || permissions[PERMISSIONS.company_delete]) && (
                <TableCell>
                  <Box className={cx('menuoption-container')}>
                    <Box className='flex flex-1'></Box>
                    <IconButton
                      onClick={(event) => {
                        openOptionMenu(event, franchises.id);
                      }}
                    >
                      <MenuVerticalIcon className='w-[19px] h-[19px]' />
                    </IconButton>
                  </Box>
                </TableCell>
              )}
            </>
          );
        }}
      />
      <MenuOption
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        handleEditItem={handleEditItem}
        handleDeleteItem={handleDeleteItem}
        enableEdit={permissions[PERMISSIONS.company_edit]}
        enableDelete={permissions[PERMISSIONS.company_delete]}
      />
    </Box>
  );
}

export default memo(FranchiseList);
